import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserHomePath } from '../../../utils/userUtils';
import '../RouteGuard/RouteGuard.css';

const ALLOW_AUTHENTICATED_PUBLIC_ROUTES = new Set([
  '/payment/processing',
  '/verify-otp',
]);
/**
 * Guard for public auth pages that redirects already signed-in users.
 *
 * This component runs AFTER AuthContext is initialized, so it can
 * use the shared auth state instead of making its own API call.
 * This eliminates race conditions and ensures consistent auth state.
 */
const AlreadySignedInGuard = () => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const allowAuthenticatedAccess = ALLOW_AUTHENTICATED_PUBLIC_ROUTES.has(
    location.pathname
  );

  if (isLoading) {
    return (
      <div className="route-guard-loading">
        <CircularProgress />
      </div>
    );
  }

  if (allowAuthenticatedAccess) {
    return <Outlet />;
  }

  if (isAuthenticated && user) {
    toast.info(t('already_signed_in'), {
      position: 'top-center',
      autoClose: 3000,
      toastId: 'already-signed-in',
    });
    const homePath = getUserHomePath(user);
    return <Navigate to={homePath} replace />;
  }

  return <Outlet />;
};

export default AlreadySignedInGuard;
